// Importação do Notion: lê o .zip exportado em "Markdown & CSV" (inclusive
// zips dentro do zip), mostra o que encontrou, deixa escolher para qual
// ambiente vai cada parte e quem é quem nas colunas de pessoa, e grava tudo
// no Ordo: árvore de páginas, bancos com propriedades, linhas com o conteúdo
// de cada task, relações entre os quadros e anexos (sem chaves e certificados).
// Importar de novo o mesmo export atualiza as páginas em vez de duplicar.
//
// A leitura do export (sem DOM) fica em notion-plan.js.

import { Marked } from "marked";
import { generateJSON } from "@tiptap/core";
import * as store from "./store.js";
import * as router from "./router.js";
import { api, uploadFile } from "./api.js";
import { el, dialog, toast } from "./dom.js";
import { baseExtensions } from "./editor.js";
import { readZip, analyze, guessUser, convertValue, parseDateTime, resolvePath, hexOf, splitMulti, optionColor } from "./notion-plan.js";
import { iconText } from "./icons.js";

const PRIVATE = "private";
const NEW = "new";

export function importDialog() {
  const file = el("input", { type: "file", accept: ".zip", hidden: true });
  const body = el("div", { class: "import-body" });
  const log = el("div", { class: "import-log" });
  const bar = el("div", { class: "progress" }, el("div", { class: "progress-fill" }));
  const choose = el("button", { class: "primary", onclick: () => file.click() }, "Escolher o arquivo .zip");
  const actions = el("div", { class: "dlg-actions" });
  let busy = false;
  const progress = (frac, text) => {
    bar.hidden = false;
    bar.firstChild.style.width = Math.round(frac * 100) + "%";
    if (text) log.prepend(el("div", {}, text));
  };
  const closeBtn = el("button", { onclick: () => { if (!busy) dlg.close(); else toast("Aguarde a importação terminar."); } }, "Fechar");
  actions.append(closeBtn);

  file.addEventListener("change", async () => {
    const f = file.files?.[0];
    if (!f) return;
    busy = true;
    choose.disabled = true;
    try {
      progress(0.02, `Lendo ${f.name}…`);
      const entries = readZip(new Uint8Array(await f.arrayBuffer()));
      const plan = await analyze(entries, { userId: store.state.me.id });
      if (!plan.nodes.size) throw new Error("Não achei páginas no zip. Exporte em \"Markdown & CSV\".");
      bar.hidden = true;
      log.replaceChildren();
      showPlan(plan, entries);
    } catch (err) {
      console.error(err);
      progress(0, "Erro: " + err.message);
      choose.disabled = false;
    } finally {
      busy = false;
    }
  });

  // Resumo do que foi encontrado e as escolhas antes de gravar.
  function showPlan(plan, entries) {
    const c = plan.counts;
    const editable = store.state.workspaces.filter(store.canEditWorkspace);
    const groupSel = new Map();
    const groupRows = plan.groups.sort((a, b) => b.total - a.total).map((g) => {
      const sel = el("select", {},
        // Só administradores do Ordo criam ambientes.
        store.state.me.role === "admin" ? el("option", { value: NEW }, `Novo ambiente "${g.name || "Notion"}"`) : null,
        el("option", { value: PRIVATE }, "Particular (só eu)"),
        ...editable.map((w) => el("option", { value: w.id }, `Ambiente ${iconText(w.icon)} ${w.name}`.replace(/\s+/g, " "))));
      const same = editable.find((w) => w.name.trim().toLowerCase() === (g.name || "").trim().toLowerCase());
      const canCreate = store.state.me.role === "admin";
      sel.value = same ? same.id : (!canCreate || !g.name || /(particular|privad|private|pessoal)/i.test(g.name)) ? PRIVATE : NEW;
      groupSel.set(g.name, sel);
      return el("div", { class: "import-row" },
        el("div", { class: "import-row-label" }, el("strong", {}, g.name || "Páginas soltas"), el("span", { class: "muted" }, ` · ${g.total} itens`)),
        sel);
    });

    const users = store.state.users;
    const peopleSel = new Map();
    const peopleRows = plan.people.map((p) => {
      const sel = el("select", {},
        el("option", { value: "" }, "Manter só o nome (etiqueta)"),
        ...users.map((u) => el("option", { value: u.id }, u.name + (u.email ? ` · ${u.email}` : ""))));
      sel.value = guessUser(p.name, users)?.id || "";
      peopleSel.set(p.name, sel);
      return el("div", { class: "import-row" },
        el("div", { class: "import-row-label" }, el("strong", {}, p.name), el("span", { class: "muted" }, ` · ${p.count}× em ${p.columns.join(", ")}`)),
        sel);
    });

    const skipSensitive = el("input", { type: "checkbox", checked: true });
    const dbList = plan.dbs.map((db) => el("li", {},
      el("strong", {}, db.title), ` · ${db.order.length} linhas`,
      db.order.filter((r) => r.md && r.md.trim()).length ? `, ${db.order.filter((r) => r.md && r.md.trim()).length} com conteúdo` : "",
      db.props.some((p) => p.type === "relation") ? ` · ligado a ${db.props.filter((p) => p.type === "relation").map((p) => `"${p.name}"`).join(", ")}` : ""));

    body.replaceChildren(
      el("div", { class: "import-summary" },
        el("p", {}, `Encontrei ${c.pages} páginas, ${c.databases} bancos de dados com ${c.rows} linhas (${c.withBody} com conteúdo) e ${c.files} arquivos.`),
        dbList.length ? el("ul", { class: "import-dbs" }, ...dbList) : null),
      el("h4", {}, "Para onde vai cada parte"),
      el("p", { class: "muted" }, "Em um ambiente, as páginas ficam visíveis para quem for membro dele. No particular, só para você."),
      ...groupRows,
      peopleRows.length ? el("h4", {}, "Quem é quem nas colunas de pessoa") : null,
      peopleRows.length ? el("p", { class: "muted" }, "Quem tiver conta no Ordo passa a receber as atribuições e os avisos de prazo.") : null,
      ...peopleRows,
      plan.sensitivePages.length ? el("label", { class: "import-check" }, skipSensitive,
        ` Não importar ${plan.sensitivePages.length} página(s) que parecem ter senhas ou acessos: `,
        el("span", { class: "muted" }, plan.sensitivePages.slice(0, 6).map((p) => p.title).join(", ") + (plan.sensitivePages.length > 6 ? "…" : ""))) : null,
      c.skippedFiles.length ? el("p", { class: "muted" }, `Arquivos de chave e certificado não são enviados: ${c.skippedFiles.join(", ")}.`) : null,
      bar, log);
    bar.hidden = true;

    const go = el("button", { class: "primary" }, "Importar");
    go.addEventListener("click", async () => {
      busy = true;
      go.disabled = true;
      for (const s of body.querySelectorAll("select, input")) s.disabled = true;
      try {
        const dest = new Map([...groupSel].map(([k, s]) => [k, s.value]));
        const peopleMap = new Map([...peopleSel].map(([k, s]) => [k, s.value]).filter(([, v]) => v));
        const res = await runImport(plan, entries, { dest, peopleMap, skipSensitive: plan.sensitivePages.length ? skipSensitive.checked : false }, progress);
        progress(1, `Pronto: ${res.pages} páginas, ${res.databases} bancos de dados, ${res.rows} linhas e ${res.files} arquivos.`);
        go.remove();
        if (res.first) actions.prepend(el("button", { class: "primary", onclick: () => { dlg.close(); router.openPage(res.first); } }, "Abrir o que foi importado"));
      } catch (err) {
        console.error(err);
        progress(0, "Erro: " + err.message);
        go.disabled = false;
      } finally {
        busy = false;
      }
    });
    actions.prepend(go);
  }

  const dlg = dialog(() => [
    el("h3", {}, "Importar do Notion"),
    body,
    el("div", { class: "dlg-actions-wrap" }, actions),
  ], { wide: true, className: "import-dialog" });
  body.append(
    el("ol", { class: "import-steps" },
      el("li", {}, "No Notion, abra ⚙ Configurações → Geral (ou o menu ⋯ de uma página) → Exportar."),
      el("li", {}, "Formato: ", el("strong", {}, "Markdown & CSV"), ". Marque \"Incluir subpáginas\" e \"Criar pastas para subpáginas\"."),
      el("li", {}, "Escolha aqui o .zip baixado. Antes de gravar, você escolhe para qual ambiente vai cada parte. Pode importar de novo depois: as páginas são atualizadas, não duplicadas.")),
    file, choose, bar, log);
  bar.hidden = true;
}

// ---------- Markdown → documento do editor ----------

// O Notion exporta o conteúdo de um bloco dentro de outro com 4 espaços de
// recuo; no Markdown comum isso viraria bloco de código. Aqui o recuo é tirado
// e o conteúdo vira blocos normais (o código do Notion sempre vem com ```).
const notionMarked = new Marked({ gfm: true, breaks: true });
notionMarked.use({
  extensions: [{
    name: "notionIndent",
    level: "block",
    tokenizer(src) {
      const m = /^(?:(?: {4}|\t)[^\n]*(?:\n|$)(?:[ \t]*\n)*)+/.exec(src);
      if (!m) return undefined;
      const raw = m[0].replace(/\n[ \t]*\n*$/, (x) => (x.startsWith("\n") ? "\n" : x));
      const text = raw.replace(/^(?: {4}|\t)/gm, "");
      return { type: "notionIndent", raw, tokens: this.lexer.blockTokens(text, []) };
    },
    renderer(token) {
      return this.parser.parse(token.tokens);
    },
  }],
});

// <aside> e <details> com Markdown dentro: linhas em branco em volta das tags
// fazem o conteúdo ser lido como Markdown.
function prepareMd(md) {
  return md
    .replace(/\r\n/g, "\n")
    .replace(/^[ \t]*(<aside>|<details>|<summary>.*<\/summary>)[ \t]*$/gim, "\n$1\n")
    .replace(/^[ \t]*(<\/aside>|<\/details>)[ \t]*$/gim, "\n$1\n");
}

export function mdToDoc(md, ctx) {
  const html = notionMarked.parse(prepareMd(md));
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  const body = doc.body;

  // <aside> (callout do Notion): o primeiro caractere costuma ser o emoji.
  for (const aside of [...body.querySelectorAll("aside")].reverse()) {
    const div = doc.createElement("div");
    div.setAttribute("data-callout", "");
    const text = aside.textContent.trim();
    const emoji = /^\p{Extended_Pictographic}(?:️|‍\p{Extended_Pictographic})*/u.exec(text)?.[0];
    if (emoji) {
      div.setAttribute("data-emoji", emoji);
      const walker = doc.createTreeWalker(aside, NodeFilter.SHOW_TEXT);
      let n;
      while ((n = walker.nextNode())) {
        if (n.textContent.includes(emoji)) { n.textContent = n.textContent.replace(emoji, "").replace(/^\s+/, ""); break; }
      }
    }
    const inner = aside.innerHTML.trim();
    div.innerHTML = /^<(p|ul|ol|h\d|blockquote|pre|table|div|details)/i.test(inner) ? inner : `<p>${inner}</p>`;
    aside.replaceWith(div);
  }
  // <details>: o conteúdo depois do <summary> vai num bloco próprio.
  for (const det of body.querySelectorAll("details")) {
    const content = doc.createElement("div");
    content.setAttribute("data-type", "detailsContent");
    for (const child of [...det.childNodes]) if (child.nodeName !== "SUMMARY") content.append(child);
    if (!content.textContent.trim() && !content.querySelector("img")) content.innerHTML = "<p></p>";
    det.append(content);
  }
  // Listas de tarefas.
  for (const ul of body.querySelectorAll("ul")) {
    const items = [...ul.children].filter((li) => li.nodeName === "LI");
    if (!items.length || !items.every((li) => li.querySelector(":scope > input[type=checkbox], :scope > p > input[type=checkbox]"))) continue;
    ul.setAttribute("data-type", "taskList");
    for (const li of items) {
      const box = li.querySelector("input[type=checkbox]");
      li.setAttribute("data-type", "taskItem");
      li.setAttribute("data-checked", box.checked || box.hasAttribute("checked") ? "true" : "false");
      box.remove();
    }
  }
  // Imagens: enviadas antes; as que não foram (ou são locais) saem.
  for (const img of body.querySelectorAll("img")) {
    const resolved = ctx.resolveAsset(img.getAttribute("src") || "");
    if (!resolved) {
      const alt = img.getAttribute("alt");
      img.replaceWith(doc.createTextNode(alt ? `[imagem não importada: ${alt}]` : ""));
      continue;
    }
    img.setAttribute("src", resolved);
    const p = img.closest("p");
    if (p && p.parentNode) p.parentNode.insertBefore(img, p);
  }
  // Links para páginas, bancos e arquivos do export.
  for (const a of body.querySelectorAll("a[href]")) {
    const href = a.getAttribute("href");
    const target = ctx.resolveLink(href);
    if (target?.page) {
      const p = a.parentElement;
      // Link sozinho na linha = bloco de página (como as subpáginas no Notion).
      if (p && p.nodeName === "P" && p.textContent.trim() === a.textContent.trim() && p.parentElement === body) {
        const block = doc.createElement("div");
        block.setAttribute("data-page-link", "");
        block.setAttribute("data-id", target.page);
        block.textContent = a.textContent;
        p.replaceWith(block);
      } else a.setAttribute("href", `ordo:page:${target.page}`);
    } else if (target?.file) a.setAttribute("href", target.file);
    else if (target?.skipped) a.replaceWith(doc.createTextNode(`${a.textContent} (não importado)`));
    else if (!/^[a-z][a-z0-9+.-]*:/i.test(href)) a.replaceWith(doc.createTextNode(a.textContent));
  }
  // <br> soltos que o Notion usa para linhas vazias.
  for (const p of body.querySelectorAll("p")) {
    if (!p.textContent.trim() && !p.querySelector("img")) p.remove();
  }
  return generateJSON(body.innerHTML, ctx.extensions);
}

// ---------- Gravação ----------

const MIME = { png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", gif: "image/gif", webp: "image/webp", svg: "image/svg+xml", pdf: "application/pdf",
  mp4: "video/mp4", mp3: "audio/mpeg", txt: "text/plain", csv: "text/csv", xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document", zip: "application/zip" };

async function runImport(plan, entries, { dest, peopleMap, skipSensitive }, progress) {
  const { nodes, dbs } = plan;

  // O que fica de fora: páginas com senhas (e o que estiver dentro delas).
  const skipped = new Set();
  if (skipSensitive) {
    const roots = new Set(plan.sensitivePages.map((p) => p.hex));
    for (const n of nodes.values()) {
      let cur = n;
      let guard = 0;
      while (cur && guard++ < 60) {
        if (roots.has(cur.hex)) { skipped.add(n.hex); break; }
        cur = cur.parentHex ? nodes.get(cur.parentHex) : null;
      }
    }
  }
  const included = [...nodes.values()].filter((n) => !skipped.has(n.hex));
  const idOf = (hex) => (nodes.has(hex) && !skipped.has(hex) ? nodes.get(hex).id : null);

  // Ambientes: cria os novos.
  const wsOf = new Map(); // grupo -> workspace_id | null
  for (const [group, choice] of dest) {
    if (choice === PRIVATE) wsOf.set(group, null);
    else if (choice === NEW) {
      const { workspace } = await store.withRetry(() => api("POST", "/workspaces", { name: group || "Notion", icon: "🗂️" }));
      wsOf.set(group, workspace.id);
      progress(0.04, `Ambiente "${workspace.name}" criado.`);
    } else wsOf.set(group, choice);
  }
  if ([...dest.values()].includes(NEW)) await store.reloadWorkspaces();

  // Colunas de pessoa: viram "Pessoa" se todos os nomes têm conta no Ordo;
  // senão, multisseleção com os nomes (nada se perde).
  for (const db of dbs) {
    for (const p of db.props) {
      if (p.type !== "person") continue;
      const names = new Set(db.order.flatMap((r) => splitMulti(r.values?.[p.name] || "")));
      if ([...names].every((n) => peopleMap.has(n))) continue;
      p.type = "multi_select";
      p.options = [...names].map((name, k) => ({ name, color: optionColor(name, k + 3) }));
    }
  }

  // Arquivos: só os citados nas páginas que vão ser importadas.
  const needed = new Map();
  for (const n of included) {
    if (!n.md) continue;
    for (const m of n.md.matchAll(/!?\[[^\]]*\]\(([^)\s]+)\)/g)) {
      const full = resolvePath(n.mdPath, m[1]);
      const a = plan.assets.get(full);
      if (a && !a.sensitive) needed.set(full, a);
    }
  }
  const urls = new Map();
  let done = 0;
  let files = 0;
  for (const a of needed.values()) {
    const ext = a.name.split(".").pop().toLowerCase();
    try {
      const blob = new Blob([entries.get(a.path)], { type: MIME[ext] || "application/octet-stream" });
      const { url } = await store.withRetry(() => uploadFile(blob, a.name));
      urls.set(a.path, url);
      files++;
    } catch (err) {
      progress(0.1, `Não consegui enviar ${a.name}: ${err.message}`);
    }
    done++;
    if (done % 5 === 0 || done === needed.size) progress(0.05 + 0.25 * (done / needed.size), `Arquivos enviados: ${done}/${needed.size}`);
  }

  // Bancos já importados antes mantêm as visões que a pessoa montou.
  const keptViews = new Map();
  for (const n of included) {
    if (n.kind !== "database" || !store.node(n.id)) continue;
    try {
      const views = (await store.load(n.id, { fresh: true })).page.schema?.views;
      if (views?.length) keptViews.set(n.id, views);
    } catch { /* página nova ou sem acesso: usa as visões padrão */ }
  }

  // Monta as páginas.
  const extensions = baseExtensions({});
  const ctxFor = (n) => ({
    extensions,
    resolveAsset: (src) => urls.get(resolvePath(n.mdPath, src)) || (/^https?:/i.test(src) ? src : null),
    resolveLink: (href) => {
      if (/^https?:\/\/(www\.)?notion\.(so|site)/i.test(href)) {
        const h = hexOf(href.split(/[?#]/)[0].replace(/-/g, ""));
        if (h && nodes.has(h)) return idOf(h) ? { page: idOf(h) } : { skipped: true };
        return null;
      }
      if (/^[a-z][a-z0-9+.-]*:/i.test(href)) return null;
      const full = resolvePath(n.mdPath, href);
      if (/\.(md|csv)$/i.test(full)) {
        const h = hexOf(full.split("/").pop().replace(/\.(md|csv)$/i, "").replace(/_all$/, ""));
        if (h && nodes.has(h)) return idOf(h) ? { page: idOf(h) } : { skipped: true };
        return null;
      }
      if (urls.has(full)) return { file: urls.get(full) };
      return plan.assets.has(full) ? { skipped: true } : null;
    },
  });

  const out = [];
  const now = Date.now();
  let k = 0;
  for (const n of included) {
    const page = {
      id: n.id, parent_id: n.parentHex ? idOf(n.parentHex) : null, kind: n.kind,
      title: n.title || n.fileTitle || "", position: now + (k++) * 10, _group: n.group,
    };
    if (n.kind === "database") {
      const statusProp = n.props.find((p) => p.type === "select" && /status|etapa|fase|situação/i.test(p.name)) || n.props.find((p) => p.type === "select");
      page.schema = {
        properties: n.props.map(({ _target, ...p }) => (_target ? { ...p, database: idOf(_target) } : p)),
        views: keptViews.get(n.id) || [
          ...(statusProp ? [{ id: "board", name: `Board por ${statusProp.name}`, type: "board", groupBy: statusProp.id }] : []),
          { id: "table", name: "Tabela", type: "table" },
        ],
        automations: [],
      };
      if (n.description) page.content = safeDoc(n.description, ctxFor(n));
    } else {
      page.content = n.md && n.md.trim() ? safeDoc(n.md, ctxFor(n)) : null;
    }
    if (n.kind === "row") {
      const db = nodes.get(n.parentHex);
      page.props = {};
      for (const p of db.props) {
        let v;
        if (p.type === "relation") v = (n.links?.[p.id] || []).map(idOf).filter(Boolean);
        else if (p.type === "person") v = convertValue(p, n.values?.[p.name], { nodes, peopleMap });
        else v = convertValue(p, n.values?.[p.name], { nodes, peopleMap });
        if (v !== undefined && v !== null && !(Array.isArray(v) && !v.length)) page.props[p.id] = v;
        if (p.type === "id") {
          const m = /(\d+)$/.exec(String(n.values?.[p.name] || "").trim());
          if (m) page.seq = Number(m[1]);
        }
        if (p.type === "created_time" && n.values?.[p.name]) page.created_at = parseDateTime(n.values[p.name]) || undefined;
      }
      page.position = (db.order.indexOf(n) + 1) * 1000;
    }
    out.push(page);
  }

  // Subpáginas na ordem em que aparecem no texto da página de cima (como no Notion).
  const byId = new Map(out.map((p) => [p.id, p]));
  for (const p of out) {
    if (!p.content) continue;
    let i = 0;
    const walk = (node) => {
      if ((node.type === "pageLink" || node.type === "databaseView") && byId.get(node.attrs?.id)?.parent_id === p.id) byId.get(node.attrs.id).position = (++i) * 1000;
      for (const c of node.content || []) walk(c);
    };
    walk(p.content);
  }
  // Linhas sem número (sem coluna de ID): numeradas na ordem do CSV.
  for (const db of dbs) {
    if (skipped.has(db.hex)) continue;
    const rows = out.filter((p) => p.parent_id === db.id).sort((a, b) => a.position - b.position);
    const used = new Set(rows.map((r) => r.seq).filter(Boolean));
    let seq = 1;
    for (const r of rows) {
      if (r.seq) continue;
      while (used.has(seq)) seq++;
      r.seq = seq++;
    }
  }

  // Pais antes dos filhos.
  const depth = (p) => {
    let d = 0;
    let cur = p;
    while (cur?.parent_id && byId.has(cur.parent_id) && d < 60) { cur = byId.get(cur.parent_id); d++; }
    return d;
  };
  out.sort((a, b) => depth(a) - depth(b));

  // Envia em lotes por ambiente (o ambiente vale para o lote inteiro).
  let sent = 0;
  for (const [group, wsId] of wsOf) {
    const list = out.filter((p) => p._group === group);
    let batch = [];
    let size = 0;
    const flush = async () => {
      if (!batch.length) return;
      const pages = batch.map(({ _group, ...p }) => p);
      await store.withRetry(() => api("POST", "/import", { pages, workspace_id: wsId }, { timeout: 120000 }));
      sent += batch.length;
      progress(0.3 + 0.65 * (sent / out.length), `Páginas gravadas: ${sent}/${out.length}`);
      batch = [];
      size = 0;
    };
    for (const p of list) {
      const s = JSON.stringify(p).length;
      if (batch.length >= 60 || size + s > 1_500_000) await flush();
      batch.push(p);
      size += s;
    }
    await flush();
  }

  // Linhas que a importação anterior criou só a partir do CSV (sem o conteúdo
  // da task): agora existem as linhas completas, então as antigas vão para a lixeira.
  const legacy = plan.legacyIds.filter((id) => !byId.has(id));
  let archived = 0;
  for (let i = 0; i < legacy.length; i += 20) {
    const r = await store.withRetry(() => api("POST", "/pages/bulk", { ids: legacy.slice(i, i + 20), archive: true }, { timeout: 120000 })).catch(() => null);
    archived += r?.done || 0;
  }
  if (archived) progress(0.97, `${archived} linha(s) repetida(s) da importação anterior foram para a lixeira.`);

  await store.loadTree();
  const first = out.find((p) => !p.parent_id && p.kind !== "row");
  return {
    pages: out.filter((p) => p.kind === "page").length,
    databases: out.filter((p) => p.kind === "database").length,
    rows: out.filter((p) => p.kind === "row").length,
    files,
    first: first?.id,
  };
}

function safeDoc(md, ctx) {
  try {
    return mdToDoc(md, ctx);
  } catch (err) {
    console.warn("conversão do markdown falhou", err);
    return { type: "doc", content: md.split(/\n{2,}/).slice(0, 2000).map((t) => ({ type: "paragraph", content: t.trim() ? [{ type: "text", text: t.slice(0, 20000) }] : [] })) };
  }
}

// Para os testes.
export const _test = { mdToDoc, prepareMd };
